import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { Box, Button } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { LocationFilledIcon } from '@sharedCR/coreDesignSystem/icons';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import { getContactTypeDialogProps } from 'app/pages/system/contactTypes/ContactTypesPage.utils';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { AddBoxIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';

import { IconSize } from 'constants/coreDesignSystem';
import { PAGE_CODES } from 'constants/pageCodes';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { getContactTypesAction } from 'store/contactTypes/actions';
import { selectContactTypesOptions } from 'store/contactTypes/selectors';
import { openDialogAction } from 'store/global/actions';
import { selectLoanContactsAllowedActions } from 'store/loanApplications/selectors';

import { NEW_RECORD_ID } from '../../../../PropertyAndLoan.contacts';
import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';
import { pagesPropTypes } from '../MainContent.constants';
import { boxStyle } from '../MainContent.styles';
import { ContentHeadline } from '../sharedComponents';

import { CREATE_NEW_CONTACT_TYPE_OPTION, getContactsConfig } from './Contacts.config';
import {
  COLUMNS_DESKTOP,
  COLUMNS_MOBILE,
  LOAN_ORIGINATION_COMPANY_CONTACT_TYPE_ID,
} from './Contacts.constants';

const Contacts = ({
  changedFields,
  isLoading,
  isSaving,
  form,
  validationErrors,
  validateField,
  handleChange,
  setEntityConfig,
  setInForm,
  className,
  staticValidateField,
  pendingChanges,
  disabledPendingChangesHighlight,
  isDeletedRecord,
}) => {
  const dispatch = useDispatch();

  const { entityId: contactId, ...restParams } = useParams();
  const history = useHistory();

  const contactTypesOptions = useSelector(selectContactTypesOptions);
  const { isManageAllowed } = useSelector(selectLoanContactsAllowedActions);
  const { isManageAllowed: isManageContactTypesAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.CONTACT_TYPES),
  );
  const { isDesktop } = useBreakpoints();

  const contactForm = form[contactId] ?? {};
  const currentContactTypeIdValue = contactForm.contactTypeId?.value;
  const isEmail = !!contactForm.email;
  const isPhoneNumber = !!contactForm.phoneNumber;
  const isCompanyContact = currentContactTypeIdValue === LOAN_ORIGINATION_COMPANY_CONTACT_TYPE_ID;

  const isCreateNewContactTypeOptionAllowed =
    isManageAllowed && isManageContactTypesAllowed && !contactTypesOptions.length;

  const adjustedContactTypesOptions = useMemo(() => {
    if (!isCreateNewContactTypeOptionAllowed) {
      return contactTypesOptions;
    }

    return [CREATE_NEW_CONTACT_TYPE_OPTION, ...contactTypesOptions];
  }, [contactTypesOptions, isCreateNewContactTypeOptionAllowed]);

  const numberOfColumns = isDesktop ? COLUMNS_DESKTOP : COLUMNS_MOBILE;

  const config = useMemo(
    () =>
      getContactsConfig({
        contactId,
        contactTypesOptions: adjustedContactTypesOptions,
        numberOfColumns,
        isEmail,
        isPhoneNumber,
        isCompanyContact,
      }),
    [
      contactId,
      adjustedContactTypesOptions,
      numberOfColumns,
      isEmail,
      isPhoneNumber,
      isCompanyContact,
    ],
  );

  const onAddClick = useCallback(() => {
    history.push(
      LoansPaths.applicationManagement.generatePath({
        ...restParams,
        entityId: NEW_RECORD_ID,
      }),
    );
  }, [history, restParams]);

  useEffectSafeStrictMode(() => {
    dispatch(getContactTypesAction());
  }, [dispatch]);

  const afterCloseContactFormCallback = useCallback(
    res => {
      const contactTypeOption = res
        ? {
            value: res.payload.id,
            name: res.payload.name,
          }
        : null;

      setInForm(`${contactId}.contactTypeId`, contactTypeOption);
    },
    [setInForm, contactId],
  );

  useEffect(() => {
    if (currentContactTypeIdValue !== CREATE_NEW_CONTACT_TYPE_OPTION.value) {
      return;
    }

    setInForm(`${contactId}.contactTypeId`, null);

    dispatch(
      openDialogAction({
        ...getContactTypeDialogProps({
          afterAcceptCallback: afterCloseContactFormCallback,
        }),
        afterCloseCallback: afterCloseContactFormCallback,
      }),
    );
  }, [dispatch, contactId, setInForm, currentContactTypeIdValue, afterCloseContactFormCallback]);

  useEffect(() => {
    if (contactId === NEW_RECORD_ID || isDeletedRecord) {
      return;
    }

    setEntityConfig(config);
  }, [config, setEntityConfig, contactId, isDeletedRecord]);

  return (
    <Box className={className}>
      <ContentHeadline
        controls={
          isManageAllowed && (
            <Button
              data-testid={PropertyAndLoanTestIds.CONTACTS_ADD_BUTTON}
              disabled={isLoading}
              size="small"
              startIcon={<AddBoxIcon size={IconSize[16]} />}
              variant="outlined"
              onClick={onAddClick}
            >
              Add Contact
            </Button>
          )
        }
        iconName={LocationFilledIcon}
        title="Contacts"
      />
      <Box sx={boxStyle}>
        <FormGenerator
          isNestedForm
          changedFields={changedFields}
          config={config}
          disabledPendingChangesHighlight={disabledPendingChangesHighlight}
          form={form}
          formName={FormNames.CONTACTS}
          handleChange={handleChange}
          isLoading={isSaving || isLoading}
          numberOfColumns={numberOfColumns}
          pendingChanges={pendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />
      </Box>
    </Box>
  );
};

Contacts.propTypes = pagesPropTypes;

export default memo(Contacts);
